import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { format, parse } from 'date-fns';
import { gungGetPropertyOfObject } from 'gung-common';
import { Availability } from '../../models/availability';
import { CalendarDate } from '../../models/calender-date';
import { CheckoutObject, CheckoutRow } from '../../models/checkout';
import { ValidationAction, ValidationRequest, ValidationResult } from '../../models/quantity-validation';
import { CartListCheckoutConfigService } from '../cart-list-checkout-config/cart-list-checkout-config.service';

@Injectable({
  providedIn: 'root'
})
export class CheckoutQuantityValidationService {
  enablePartialDeliveries = false;
  enablePushDeliveries = true;

  constructor(protected translateService: TranslateService, protected cartListCheckoutConfigService: CartListCheckoutConfigService) { }

  public getLowerAvailabilityAction(
    validationResult: ValidationResult,
    validationRequest?: ValidationRequest
  ): ValidationAction {
    const availability = this.getAvailabilityValue(validationRequest);
    return {
      id: 'LOWER_QUANTITY',
      description: this.translateService.instant('LOWER_QUANTITY') + ' -> ' + availability,
      callback(): void {
        validationResult.row.quantity = availability;
      }
    };
  }

  public getLowerAvailabilityFutureStockAction(
    validationResult: ValidationResult,
    validationRequest?: ValidationRequest
  ): ValidationAction {
    const availability = validationResult.availability.maxFutureStock;
    return {
      id: 'FUTURE_LOWER_QUANTITY',
      description: this.translateService.instant('AVAILABLE_IN_FUTURE_LOWER_QUANTITY') + ' -> ' + availability,
      callback(): void {
        validationResult.row.quantity = availability;
      }
    };
  }

  public getNOOPAction(validationResult: ValidationResult): ValidationAction {
    return {
      description: this.translateService.instant('NO_ACTION_SELECT_ACTION'),
      callback(): void {
        // Do nothing, used as first action when you want force users to select something
      }
    };
  }

  public getRemoveProductAction(
    validationResult: ValidationResult,
    validationRequest?: ValidationRequest
  ): ValidationAction {
    return {
      id: 'REMOVE',
      description: this.translateService.instant('REMOVE_PRODUCT'),
      callback(): void {
        validationResult.row.quantity = 0;
      }
    };
  }

  public getQtyValidationResult(validationRequest: ValidationRequest): ValidationResult {
    const validationResult: ValidationResult = {
      row: validationRequest.row,
      product: validationRequest.product,
      availability: validationRequest.availability,
      valid: true,
      actions: []
    };

    if (!this.shouldValidate(validationRequest)) {
      return validationResult;
    }

    const availabilityValue = this.getAvailabilityValue(validationRequest);

    if (availabilityValue < validationRequest.row.quantity) {
      // LET'S CHECK IF ANY QTY AVAILABLE IN THE FUTURE HIGHER OR EQUAL THAN REQUESTED QTY
      // IF YES
      //    THEN IF ORDER DELIVERY DATE IS ON_OR_AFTER THAN THE DATE WHEN THE FUTURE AVAILABILITY IS AVAILABLE
      //        THEN
      //            RETURN OK
      //        OTHERWISE
      //            ADD PUSH DELIREY DATE ACTION
      // OTHERWISE
      //    DO NOT ADD THE ACTION TO PUSH DELIVERY DATE
      let addDeliveryPlanAction = false;
      const deliveryDate = this.getRowDeliveryDate(validationRequest.row, validationRequest.checkout);
      const futureAvailabilityPlan = this.getFutureAvailabilityPlan(
        deliveryDate,
        validationRequest.availability,
        validationRequest.row.quantity
      );

      const planSuggested = {};
      let hasAnyQty = false;
      for (const element of futureAvailabilityPlan) {
        planSuggested[format(element.date, 'yyyy-MM-dd')] = element.qty;
        hasAnyQty = hasAnyQty || element.qty > 0;
      }

      const planOnRow = validationRequest.row.extra._deliveryPlan;
      const isPlanEqual = JSON.stringify(planSuggested) === JSON.stringify(planOnRow);
      if (isPlanEqual) {
        return validationResult;
      }

      if (hasAnyQty && futureAvailabilityPlan.length > 0) {
        addDeliveryPlanAction = true;
      }

      let addPushDeliveryDateAction = false;
      if (validationRequest.availability.maxFutureStock >= validationRequest.row.quantity) {
        const deliveryDate = this.getRowDeliveryDate(validationRequest.row, validationRequest.checkout);
        const futureAvailability = this.getFutureAvailability(
          validationRequest.availability,
          validationRequest.row.quantity
        );
        if (deliveryDate >= futureAvailability.date) {
          // DELIVERY DATE FOR THIS ITEM IS ON_OR_AFTER THAN IT WILL BE AVAILABLE IN FUTURE
          return validationResult;
        }

        addPushDeliveryDateAction = true;
      }

      // set not valid action
      validationResult.valid = false;
      validationResult.description =
        this.translateService.instant('NOT_ENOUGH_AVAILABILITY') +
        ' (' +
        validationResult.row.quantity +
        '>' +
        availabilityValue +
        ')';

      if (availabilityValue > 0) {
        // Reduce quantity not relevant if no quantity exists
        validationResult.actions.push(this.getLowerAvailabilityAction(validationResult, validationRequest));
      } else if (
        validationRequest.availability.maxFutureStock > 0 &&
        validationRequest.availability.maxFutureStock < validationRequest.row.quantity
      ) {
        // only add if not added the action to lower availability above not added
        validationResult.description =
          this.translateService.instant('NOT_ENOUGH_AVAILABILITY') +
          ' (' +
          validationResult.row.quantity +
          '>' +
          validationRequest.availability.maxFutureStock +
          ')';
        validationResult.actions.push(this.getLowerAvailabilityFutureStockAction(validationResult, validationRequest));
      }

      if (addDeliveryPlanAction && this.enablePartialDeliveries) {
        validationResult.actions.push(this.getDeliveryPlanAction(validationResult, validationRequest));
      }

      if (addPushDeliveryDateAction && this.enablePushDeliveries) {
        validationResult.actions.push(this.getPushDeliveryDateAction(validationResult));
      }

      validationResult.actions.push(this.getRemoveProductAction(validationResult, validationRequest));

      validationResult.selectedAction = validationResult.actions[0];
    }
    return validationResult;
  }

  protected shouldValidate(validationRequest: ValidationRequest): boolean {
    return true;
  }

  protected getAvailabilityValue(validationRequest: ValidationRequest): number {
    return validationRequest.availability.currentAvailability;
  }

  public transformAvailabilityDates(
    sourceAvailabilityMap: any,
    firstDate: Date,
    calendarValidDeliveryDates: CalendarDate[]
  ): any {
    const firstDateStr = format(firstDate || new Date(), 'yyyy-MM-dd');
    const firstDateWithoutTime = parse(firstDateStr, 'yyyy-MM-dd', firstDate);

    let validDatesMapped = null;
    if (calendarValidDeliveryDates) {
      validDatesMapped = {};
      for (const theDate of calendarValidDeliveryDates) {
        if (theDate.valid) {
          validDatesMapped[format(theDate.date, 'yyMMdd')] = true;
        }
      }
    }

    const remapped = {};

    // Somewhat randomly chosen considering only the fact that jeeves calendar cycles at lest every two weeks
    const MAX_ATTEMPTS = 35;

    for (const key of Object.keys(sourceAvailabilityMap)) {
      const futureDate = parse(key, 'yyMMdd', new Date());
      let newkey = key;
      if (futureDate <= firstDateWithoutTime) {
        newkey = format(firstDateWithoutTime, 'yyMMdd');
      }

      if (validDatesMapped) {
        let attempts = 0;
        while (!validDatesMapped[newkey] && attempts < MAX_ATTEMPTS) {
          var theDate = parse(newkey, 'yyMMdd', new Date());
          var newDate = new Date(theDate.getTime() + 1000 * 60 * 60 * 24);
          newkey = format(newDate, 'yyMMdd');
          attempts++;
        }
      }

      remapped[newkey] = sourceAvailabilityMap[key];
    }
    return remapped;
  }

  public getFutureAvailability(
    availability: Availability,
    requiredQty: number,
    calendarValidDeliveryDates: CalendarDate[] = null
  ): { qty: number; date: Date } {
    const remapped = this.transformAvailabilityDates(
      availability.extra.availabilities,
      null,
      calendarValidDeliveryDates
    );

    let result = { qty: 0, date: new Date(0) };
    for (const key of Object.keys(remapped)) {
      const value = remapped[key];
      if (requiredQty > result.qty) {
        if (value >= requiredQty) {
          const futureDate = parse('20' + key, 'yyyyMMdd', new Date());
          result = {
            qty: parseInt(value, 10),
            date: futureDate
          };
        }
      }
    }
    return result;
  }

  public getFutureAvailabilityPlan(
    firstDate: Date,
    availability: Availability,
    requiredQty: number,
    calendarValidDeliveryDates: CalendarDate[] = null
  ): { qty: number; date: Date }[] {
    const result = [];
    let leftToAllocate = requiredQty;

    // Transform the list of availabilities according to current calendar (and selected date)
    const remapped = this.transformAvailabilityDates(
      availability.extra.availabilities,
      firstDate,
      calendarValidDeliveryDates
    );

    for (const key of Object.keys(remapped)) {
      const value = remapped[key];

      if (leftToAllocate > 0) {
        const futureDate = parse(key, 'yyMMdd', new Date());

        const allocation = Math.min(leftToAllocate, value);
        leftToAllocate = leftToAllocate - allocation;
        const element = {
          qty: allocation,
          date: futureDate
        };
        result.push(element);
      }
    }

    return result;
  }

  public getDeliveryPlanAction(
    validationResult: ValidationResult,
    validationRequest: ValidationRequest
  ): ValidationAction {
    const deliveryDate = this.getRowDeliveryDate(validationRequest.row, validationRequest.checkout);
    const futureAvailabilityPlan = this.getFutureAvailabilityPlan(
      deliveryDate,
      validationRequest.availability,
      validationRequest.row.quantity
    );

    const deliveryPlan = {};
    let lastDate = '';
    for (const element of futureAvailabilityPlan) {
      deliveryPlan[format(element.date, 'yyyy-MM-dd')] = element.qty;
      lastDate = format(element.date, 'yyyy-MM-dd');
    }

    return {
      id: 'PARTIAL_DELIVERY',
      description: this.translateService.instant('PARTIAL_DELIVERIES_ASAP') + ' (' + lastDate + ')',
      callback(): void {
        validationResult.row.extra._deliveryPlan = deliveryPlan;
      }
    };
  }

  public getPushDeliveryDateAction(validationResult: ValidationResult): ValidationAction {
    const futureDeliveryDate = this.getFutureAvailability(
      validationResult.availability,
      validationResult.row.quantity
    ).date;
    const futureDeliveryDateStr = format(futureDeliveryDate, 'yyyy-MM-dd');
    validationResult.row.extra.futureDeliveryDate = futureDeliveryDateStr;
    return {
      id: 'PUSH_DELIVERY',
      description: this.translateService.instant('PUSH_DELIVERY_DATE') + ' -> ' + futureDeliveryDateStr,
      callback(): void {
        validationResult.row.extra.gme.deliveryDate = futureDeliveryDateStr;
        delete validationResult.row.extra.futureDeliveryDate;
      }
    };
  }

  public getRowDeliveryDate(row: CheckoutRow, checkout: CheckoutObject): Date {
    row.extra.gme = row.extra.gme || {};
    if (!row.extra.gme.deliveryDate && checkout.extra.gungSalesOrders?.deliveryDate) {
      row.extra.gme.deliveryDate = checkout.extra.gungSalesOrders.deliveryDate;
    }
    let deliveryDate = gungGetPropertyOfObject('extra.' + this.cartListCheckoutConfigService.deliveryDateField, row);
    if (!row.extra.gme.deliveryDate) {
      row.extra.gme.deliveryDate = deliveryDate;
    }

    return row.extra.gme.deliveryDate
      ? parse(row.extra.gme.deliveryDate, 'yyyy-MM-dd', new Date())
      : new Date();
  }
}
